<script setup lang="ts">
import { storeToRefs } from "pinia"
import { onUnmounted, ref, watch } from "vue"
import { useSetupStore } from "@/stores"

const emit = defineEmits<Emits>()
const setupStore = useSetupStore()
const lastTarget = ref<EventTarget | null>()
const dropzoneRef = ref<HTMLElement>()

interface Emits {
  (event: "fileChanged", files: File[]): void
}

const { setupDialog } = storeToRefs(setupStore)

function isFile(event: DragEvent) {
  const dt = event.dataTransfer
  if (dt === null) {
    return false
  }
  return dt.types.includes("Files")
}

function handleDragEnter(event: DragEvent) {
  if (isFile(event) && dropzoneRef.value && !setupDialog.value) {
    lastTarget.value = event.target
    dropzoneRef.value.style.visibility = ""
    dropzoneRef.value.style.opacity = "1"
  }
}

function handleDragLeave(event: DragEvent) {
  event.preventDefault()
  if (
    dropzoneRef.value &&
    (event.target === lastTarget.value || event.target === document) &&
    !setupDialog.value
  ) {
    dropzoneRef.value.style.visibility = "hidden"
    dropzoneRef.value.style.opacity = "0"
  }
}

function handleDragOver(event: DragEvent) {
  if (!setupDialog.value) {
    event.preventDefault()
  }
}

function handleDrop(event: DragEvent) {
  if (setupDialog.value) {
    return
  }
  event.preventDefault()
  if (dropzoneRef.value) {
    dropzoneRef.value.style.visibility = "hidden"
    dropzoneRef.value.style.opacity = "0"
  }

  if (
    event.dataTransfer?.files !== undefined &&
    event.dataTransfer.files.length > 0
  ) {
    emit("fileChanged", Array.from(event.dataTransfer.files))
  }
}

watch(
  () => setupDialog.value,
  (isDialogOpen) => {
    if (isDialogOpen) {
      window.removeEventListener("dragenter", handleDragEnter)
      window.removeEventListener("dragleave", handleDragLeave)
      window.removeEventListener("dragover", handleDragOver)
      window.removeEventListener("drop", handleDrop)
      if (dropzoneRef.value) {
        dropzoneRef.value.style.visibility = "hidden"
        dropzoneRef.value.style.opacity = "0"
      }
    } else {
      window.addEventListener("dragenter", handleDragEnter)
      window.addEventListener("dragleave", handleDragLeave)
      window.addEventListener("dragover", handleDragOver)
      window.addEventListener("drop", handleDrop)
    }
  },
  { immediate: true }
)

onUnmounted(() => {
  window.removeEventListener("dragenter", handleDragEnter)
  window.removeEventListener("dragleave", handleDragLeave)
  window.removeEventListener("dragover", handleDragOver)
  window.removeEventListener("drop", handleDrop)
})

const globalDropStyle = "fixed inset-0 z-9999 grid content-center bg-black/50 text-center text-4xl text-white transition-colors"
</script>

<template>
  <div
    ref="dropzoneRef"
    style="visibility: hidden; opacity: 0"
    :class="setupDialog ? 'relative text-lg text-dark' : globalDropStyle"
  >
    <div>Drop anywhere!</div>
  </div>
</template>
